import { useState, useCallback, useOptimistic, useMemo } from 'react';
import {
  Input,
  Button,
  VStack,
  Box,
  Icon,
  Separator,
  Grid,
  AbsoluteCenter,
  Text,
  Flex,
} from '@chakra-ui/react';
import {
  PlusIcon,
  TrashIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { Field } from '@/components/ui/field';
import { Tooltip } from '@/components/ui/tooltip';
import useModalToggle from '@/lib/hooks/use-modal-toggle';
import ContentList from '@/components/fillable-blocks/section-blocks/content-list';
import ContentBlock from '@/components/fillable-blocks/section-blocks/content-block';
import { ContentType, Content, GroupForm } from '@/lib/types';

interface Props {
  groups: GroupForm[];
  errors: any;
  contentTypes: ContentType[];
  workspaceId: number;
  contents: Content[];
  onChange(groups: GroupForm[], item?: Content): void;
}

export default function GroupBlock({
  groups,
  errors,
  contentTypes,
  workspaceId,
  contents,
  onChange,
}: Props) {
  const [contentType, setContentType] = useState<ContentType>();
  const [selectedGroup, setSelectedGroup] = useState<GroupForm>();
  const [selectedIndex, setSelectedIndex] = useState<number>();

  const contentBlockDrawer = useModalToggle();

  const [optimisticGroups, addOptimisticGroup] = useOptimistic(
    groups,
    (state, newGroup: GroupForm) => [...state, newGroup],
  );

  const hasGroups = useMemo(() => groups.length > 0, [groups]);
  const hasContentTypes = useMemo(() => contentTypes.length > 0, [contentTypes]);

  const addContent = useCallback(
    (refs: string[], item: Content) => {
      if (!selectedGroup || selectedIndex === null || selectedIndex === undefined) return;

      const updatedGroup = {
        ...selectedGroup,
        contents: refs,
      };

      const updatedGroups = [...groups];
      updatedGroups[selectedIndex] = updatedGroup;
      onChange(updatedGroups, item);
    },
    [selectedGroup, selectedIndex, groups, onChange],
  );

  const applyChanges = useCallback(
    (groupItem: GroupForm, groupIndex: number) => {
      const updatedGroups = groups.map((group, index) =>
        index === groupIndex ? groupItem : group,
      );
      onChange(updatedGroups);
    },
    [groups, onChange],
  );

  const addGroup = () => {
    const newGroup = {
      name: '',
      contents: [],
    };

    addOptimisticGroup(newGroup);
    onChange([...groups, newGroup]);
  };

  const removeGroup = (groupIndex: number) => {
    const updatedGroups = groups.filter((_, index) => index !== groupIndex);
    onChange(updatedGroups);
  };

  const openContentSelector = useCallback(
    (type: ContentType, group: GroupForm, index: number) => {
      setContentType(type);
      setSelectedGroup(group);
      setSelectedIndex(index);
      contentBlockDrawer.open();
    },
    [contentBlockDrawer],
  );

  const updateGroupName = (value: string, groupIndex: number) => {
    const updatedGroups = groups.map((group, index) =>
      index === groupIndex ? { ...group, name: value } : group,
    );
    onChange(updatedGroups);
  };

  const removeItem = useCallback(
    (groupIndex: number, referenceId: string) => {
      if (groupIndex < 0 || groupIndex >= groups.length) {
        return;
      }

      const currentGroup = groups[groupIndex];
      const updatedContents = currentGroup.contents.filter((id) => id !== referenceId);
      const updatedGroup = {
        ...currentGroup,
        contents: updatedContents,
      };

      const updatedGroups = [...groups];
      updatedGroups[groupIndex] = updatedGroup;

      onChange(updatedGroups);
    },
    [groups, onChange],
  );

  return (
    <Box mt={6}>
      <Box position='relative' py={4} w='full' mb={3}>
        <Separator borderColor='border' />
        <AbsoluteCenter bg='bg.panel' px='4'>
          Content Groups
        </AbsoluteCenter>
      </Box>

      {!hasGroups && (
        <Box
          p={6}
          borderWidth='1px'
          borderStyle='dashed'
          borderColor='border'
          borderRadius='md'
          textAlign='center'
          mb={6}
          bg='bg.subtle'
        >
          <Icon as={DocumentTextIcon} w={8} h={8} color='fg.muted' mb={3} />
          <Text mb={4} color='fg.muted'>
            No content groups added yet.
          </Text>
          <Button onClick={addGroup} variant='primary'>
            <Icon as={PlusIcon} w={4} h={4} />
            Add Your First Group
          </Button>
        </Box>
      )}

      {optimisticGroups.map((item, groupIndex) => (
        <Box key={`group-${groupIndex}`} w='full' position='relative'>
          <VStack gap={4} align={'stretch'}>
            <Flex gap={3}>
              <Field
                invalid={errors && !!errors[groupIndex]?.name}
                errorText={errors && errors[groupIndex]?.name?.message}
              >
                <Input
                  id={`group-name-${groupIndex}`}
                  value={item.name}
                  placeholder={'Name this content group...'}
                  aria-label={`Group ${groupIndex + 1} name`}
                  onChange={(e) => updateGroupName(e.target.value, groupIndex)}
                />
              </Field>

              <Tooltip content='Remove this group'>
                <Button
                  variant='ghost'
                  colorPalette='red'
                  size='sm'
                  onClick={() => removeGroup(groupIndex)}
                  aria-label={`Remove group ${groupIndex + 1}`}
                >
                  <Icon as={TrashIcon} w={4} h={4} />
                </Button>
              </Tooltip>
            </Flex>
            <VStack gap={2} align='stretch'>
              <ContentList
                groupItem={item}
                groupIndex={groupIndex}
                contents={contents || []}
                onChange={applyChanges}
                removeItem={removeItem}
              />
            </VStack>

            {hasContentTypes ? (
              <Grid
                templateColumns={{
                  base: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                  lg: 'repeat(4, 1fr)',
                }}
                gap={4}
              >
                {contentTypes.map((type) => (
                  <Button
                    key={type.id}
                    variant='outline'
                    size='sm'
                    justifyContent='flex-start'
                    textOverflow='ellipsis'
                    overflow='hidden'
                    whiteSpace='nowrap'
                    onClick={() => openContentSelector(type, item, groupIndex)}
                  >
                    <Icon as={PlusIcon} w={4} h={4} />
                    {type.name}
                  </Button>
                ))}
              </Grid>
            ) : (
              <Box
                p={4}
                borderWidth='1px'
                borderStyle='dashed'
                borderColor='border'
                bg='bg.subtle'
                borderRadius='md'
                textAlign='center'
              >
                <Flex align='center' justify='center' color='fg.muted'>
                  <Icon as={ExclamationCircleIcon} mr={2} w={5} h={5} />
                  <Text>No content types available.</Text>
                </Flex>
              </Box>
            )}
          </VStack>
          <Separator borderColor='border' my={8} />
        </Box>
      ))}

      <Button
        onClick={addGroup}
        variant='outline'
        width='full'
        mb={6}
        display={hasGroups ? 'flex' : 'none'}
      >
        <Icon as={PlusIcon} w={4} h={4} />
        Add Content Group
      </Button>

      {workspaceId && (
        <ContentBlock
          {...contentBlockDrawer.props}
          workspaceId={workspaceId}
          contentType={contentType}
          contentRefs={selectedGroup?.contents || []}
          showPartner={true}
          onSuccess={addContent}
        />
      )}
    </Box>
  );
}
